import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface AnimatedCounterProps {
  value: string;
  className?: string;
  showCursor?: boolean;
  cursorDuration?: number;
}

const parseValue = (value: string): { prefix: string; number: number; suffix: string } => {
  const match = value.match(/^([^\d]*)([\d,]+\.?\d*)(.*)$/);
  if (!match) return { prefix: "", number: 0, suffix: "" };
  
  return {
    prefix: match[1] || "",
    number: parseFloat(match[2].replace(/,/g, "")) || 0,
    suffix: match[3] || "",
  };
};

const formatNumber = (num: number, hasDecimal: boolean): string => {
  if (hasDecimal) {
    return num.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  }
  return Math.round(num).toLocaleString("en-US");
};

export const AnimatedCounter = ({ 
  value, 
  className,
  showCursor = false,
  cursorDuration = 2000,
}: AnimatedCounterProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [displayValue, setDisplayValue] = useState("0");
  const [showCursorState, setShowCursorState] = useState(false);
  
  const { prefix, number, suffix } = parseValue(value);
  const hasDecimal = value.includes(".");

  // Simple intersection observer
  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          setDisplayValue(formatNumber(number, hasDecimal));
          if (showCursor) {
            setShowCursorState(true);
          }
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "-50px" }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [number, hasDecimal, showCursor]);

  // Fade out cursor after duration
  useEffect(() => {
    if (showCursorState && cursorDuration > 0) {
      const timer = setTimeout(() => {
        setShowCursorState(false);
      }, cursorDuration);
      return () => clearTimeout(timer);
    }
  }, [showCursorState, cursorDuration]);

  return (
    <span
      ref={ref}
      className={cn(
        "font-mono transition-all duration-700 ease-out inline-flex items-baseline",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2",
        className
      )}
    >
      {prefix}
      {isVisible ? displayValue : "0"}
      {suffix}
      {showCursorState && (
        <span 
          className="text-primary animate-cursor-blink pointer-events-none ml-0.5"
          aria-hidden="true"
        >
          _
        </span>
      )}
    </span>
  );
};

export default AnimatedCounter;
